import { useParentPosition } from "@/hooks/useParentPosition";
import { useEffect, useRef, useState } from "react";
import { IconChevronDown } from "@tabler/icons-react";

type GuestStatusInputProps = {
  options: string[];
  initialValue: string;
  getKeyFromDisplayValue: (displayValue: string) => string;
  setParent: (value: string) => void;
  onFocusAction?: () => void;
  height?: string;
  border?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  blank?: boolean;
  disabled?: boolean;
};

export const GuestStatusInput: React.FC<GuestStatusInputProps> = ({
  options,
  initialValue,
  getKeyFromDisplayValue,
  setParent,
  onFocusAction,
  height = "h-9",
  border = "",
  surface = "surface-primary",
  fontSize = "body-2-regular",
  padding = "px-2 py-1",
  rounded = "rounded-lg",
  blank,
  disabled,
}) => {
  const isMouseDownOnDropdown = useRef(false);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(initialValue);
  const [selectedValueKey, setSelectedValueKey] = useState<string>(
    getKeyFromDisplayValue(initialValue),
  );
  const [inputWidth, setInputWidth] = useState<number>(0);
  const [positionReady, setPositionReady] = useState(false);

  // Hook for positioning
  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const divRef = useRef<HTMLDivElement>(null);

  // The value can be replaced from OUTSIDE, and this control has to follow it.
  // The filter row is where that shows: clearing the filter (the ✕ on the
  // count pill, or removing the row in the filter popup) empties the cell, but
  // the box went on printing the status that had just been taken away — a
  // guest list that says it is filtered by "Accepted" while it is not.
  // `initialValue` is only the SEED for a local pick; a changed one is a new
  // value, not a re-render of the old one. Keyed on the prop alone, so a pick
  // the parent does not echo back (a table row parks inline edits in a pending
  // batch, not in the row's value) leaves the prop unchanged and the pick
  // stands. It does NOT stand against a value that genuinely moves underneath
  // an open edit — a background refetch that rewrites the row will repaint the
  // box while the pending batch still holds the pick. That is what every other
  // cell editor here does (OptionInput, TCCString, TCCDate), so this control
  // stops being the exception rather than inventing a rule of its own.
  useEffect(() => {
    setSelectedValue(initialValue);
  }, [initialValue]);

  // Sync selected key
  useEffect(() => {
    setSelectedValueKey(getKeyFromDisplayValue(selectedValue));
  }, [selectedValue, getKeyFromDisplayValue]);

  // --- Live Tracking Effect (Scroll & Resize) ---
  useEffect(() => {
    if (showOptions && divRef.current) {
      const updatePosition = () => {
        if (divRef.current) setDropdownPosition(divRef);
      };

      // Capture phase is required for nested scrolling
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showOptions, setDropdownPosition]);

  // --- Immediate Failsafe Effect ---
  useEffect(() => {
    if (showOptions && divRef.current) {
      // Force calculation immediately when state becomes true
      setDropdownPosition(divRef);
      // Sync width
      setInputWidth(divRef.current.offsetWidth);
    }
  }, [showOptions, setDropdownPosition]);

  // --- Wait for position to be calculated before marking ready ---
  useEffect(() => {
    if (showOptions && dropdownPosition.top !== 0) {
      setPositionReady(true);
    }
  }, [showOptions, dropdownPosition]);

  // --- Centralized Open Logic ---
  const handleToggle = () => {
    // Prevent toggling when disabled is true
    if (disabled) return;

    if (!showOptions) {
      // Opening Sequence:
      if (onFocusAction) onFocusAction();

      // 1. Calculate position FIRST
      if (divRef.current) setDropdownPosition(divRef);

      // 2. Then show component
      setPositionReady(false);
      setShowOptions(true);
    } else {
      // Closing
      setShowOptions(false);
      setPositionReady(false);
    }
  };

  const handleSelection = (value: string) => {
    // Prevent selection when disabled is true
    if (disabled) return;

    setSelectedValue(value);
    setParent(value);
    setShowOptions(false);
    setPositionReady(false);
  };

  return (
    <>
      <div className="relative w-full">
        <p>{blank}</p>
        <div
          ref={divRef}
          onClick={handleToggle}
          onMouseEnter={() => !disabled && setDropdownPosition(divRef)}
          className={`${padding} ${surface} ${rounded} ${height} ${border} w-full flex items-center ${fontSize} pr-8
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            ${
              blank
                ? ""
                : selectedValueKey === "UNCONTACTED"
                  ? `user-icon-font-1 user-icon-bg-1 ${
                      border === "border" ? "outline-user-icon-1" : ""
                    }`
                  : selectedValueKey === "CONTACTED"
                    ? `user-icon-font-2 user-icon-bg-2 ${
                        border === "border" ? "outline-user-icon-2" : ""
                      }`
                    : selectedValueKey === "ACCEPTED"
                      ? `user-icon-font-4 user-icon-bg-4 ${
                          border === "border" ? "outline-user-icon-4" : ""
                        }`
                      : selectedValueKey === "DECLINED"
                        ? `user-icon-font-3 user-icon-bg-3 ${
                            border === "border" ? "outline-user-icon-3" : ""
                          }`
                        : selectedValueKey === "WAITLISTED"
                          ? `user-icon-font-12 user-icon-bg-12 ${
                              border === "border" ? "outline-user-icon-12" : ""
                            }`
                        : ""
            }`}
        >
          <div className="flex flex-row w-full">
            <p>{selectedValue}</p>
          </div>
        </div>
        <IconChevronDown
          size={16}
          className={`${
            blank
              ? ""
              : selectedValueKey === "UNCONTACTED"
                ? `user-icon-font-1`
                : selectedValueKey === "CONTACTED"
                  ? `user-icon-font-2`
                  : selectedValueKey === "ACCEPTED"
                    ? `user-icon-font-4`
                    : selectedValueKey === "DECLINED"
                      ? `user-icon-font-3`
                      : selectedValueKey === "WAITLISTED"
                        ? `user-icon-font-12`
                      : ""
          } absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${
            showOptions ? "rotate-180" : ""
          } ${disabled ? "opacity-50" : ""}`}
        />
      </div>

      {showOptions && !disabled && positionReady && (
        <div
          className="fixed z-20 inset-0 h-screen w-screen"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ul
            className="fixed p-2 z-20 space-y-2 mt-1 surface-primary border rounded-lg max-h-60 overflow-y-auto outline-quaternary"
            style={{
              ...(dropdownPosition.showAbove
                ? {
                    bottom: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }
                : {
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }),
              minWidth: `${inputWidth}px`,
              width: "max-content",
              maxWidth: `min(20rem, calc(100vw - ${dropdownPosition.left}px - 16px))`,
            }}
            onMouseDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
          >
            {options.map((option, index) => (
              <li
                key={`option-${index}`}
                className={`py-1 cursor-pointer w-full  body-3-regular px-2 flex rounded-lg items-center justify-between
                  ${
                    blank
                      ? ""
                      : getKeyFromDisplayValue(option) === "UNCONTACTED"
                        ? "user-icon-font-1 user-icon-bg-1"
                        : getKeyFromDisplayValue(option) === "CONTACTED"
                          ? "user-icon-font-2 user-icon-bg-2"
                          : getKeyFromDisplayValue(option) === "ACCEPTED"
                            ? "user-icon-font-4 user-icon-bg-4"
                            : getKeyFromDisplayValue(option) === "DECLINED"
                              ? "user-icon-font-3 user-icon-bg-3"
                              : getKeyFromDisplayValue(option) ===
                                  "WAITLISTED"
                                ? "user-icon-font-12 user-icon-bg-12"
                              : ""
                  }
                  ${selectedValue === option ? "" : ""}`}
                onClick={() => handleSelection(option)}
              >
                <span>{option}</span>
              </li>
            ))}
          </ul>
          <div
            className="fixed inset-0 h-screen w-screen"
            onClick={() => {
              setShowOptions(false);
              setPositionReady(false);
            }}
          />
        </div>
      )}
    </>
  );
};
